import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import {
  DataConflictSettings,
  ExportPermissions,
  NotificationSettings,
} from '../types';

interface AdvancedSettingsCardProps {
  conflictSettings: DataConflictSettings;
  exportPermissions: ExportPermissions;
  notificationSettings: NotificationSettings;
  onUpdateConflict: (settings: DataConflictSettings) => void;
  onUpdateExport: (settings: ExportPermissions) => void;
  onUpdateNotifications: (settings: NotificationSettings) => void;
}

const AdvancedSettingsCard = ({
  conflictSettings,
  exportPermissions,
  notificationSettings,
  onUpdateConflict,
  onUpdateExport,
  onUpdateNotifications,
}: AdvancedSettingsCardProps) => {
  const [activeTab, setActiveTab] = useState<'conflict' | 'export' | 'notifications'>('conflict');
  const [isEditing, setIsEditing] = useState(false);
  const [conflictForm, setConflictForm] = useState(conflictSettings);
  const [exportForm, setExportForm] = useState(exportPermissions);
  const [notificationForm, setNotificationForm] = useState(notificationSettings);

  const resolutionOptions = [
    { value: 'admin-override', label: 'Admin Override' },
    { value: 'latest-wins', label: 'Latest Wins' },
    { value: 'manual-review', label: 'Manual Review' },
  ];

  const exportFormatOptions = [
    { value: 'csv', label: 'CSV' },
    { value: 'excel', label: 'Excel' },
    { value: 'pdf', label: 'PDF' },
  ];

  const handleSave = () => {
    if (activeTab === 'conflict') {
      onUpdateConflict(conflictForm);
    } else if (activeTab === 'export') {
      onUpdateExport(exportForm);
    } else {
      onUpdateNotifications(notificationForm);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setConflictForm(conflictSettings);
    setExportForm(exportPermissions);
    setNotificationForm(notificationSettings);
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-md border border-border shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center">
              <Icon name="Settings" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Advanced Settings</h2>
              <p className="text-sm text-muted-foreground">
                Configure advanced group features
              </p>
            </div>
          </div>
          {!isEditing && (
            <Button
              variant="outline"
              iconName="Edit"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>

        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('conflict')}
            className={`px-4 py-2 text-sm font-medium transition-interactive ${
              activeTab === 'conflict' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Data Conflicts
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={`px-4 py-2 text-sm font-medium transition-interactive ${
              activeTab === 'export'
                ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Export Permissions
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 text-sm font-medium transition-interactive ${
              activeTab === 'notifications'
                ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Notifications
          </button>
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'conflict' && (
          <div className="space-y-4">
            {isEditing ? (
              <>
                <Select
                  label="Resolution Strategy"
                  description="How to handle data conflicts"
                  options={resolutionOptions}
                  value={conflictForm.resolutionStrategy}
                  onChange={(value) =>
                    setConflictForm({
                      ...conflictForm,
                      resolutionStrategy: value as DataConflictSettings['resolutionStrategy'],
                    })
                  }
                />
                <Checkbox
                  label="Notify on Conflict"
                  description="Send notifications when conflicts occur"
                  checked={conflictForm.notifyOnConflict}
                  onChange={(e) =>
                    setConflictForm({ ...conflictForm, notifyOnConflict: e.target.checked })
                  }
                />
                <Checkbox
                  label="Auto-Resolve Minor Conflicts"
                  description="Automatically resolve minor data conflicts"
                  checked={conflictForm.autoResolveMinor}
                  onChange={(e) =>
                    setConflictForm({ ...conflictForm, autoResolveMinor: e.target.checked })
                  }
                />
              </>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-muted-foreground">
                    Resolution Strategy
                  </span>
                  <p className="text-base text-foreground mt-1">
                    {resolutionOptions.find((o) => o.value === conflictSettings.resolutionStrategy)?.label}
                  </p>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">Notify on Conflict</span>
                  <Icon
                    name={conflictSettings.notifyOnConflict ? 'CheckCircle' : 'Circle'}
                    size={20}
                    color={
                      conflictSettings.notifyOnConflict
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">
                    Auto-Resolve Minor
                  </span>
                  <Icon
                    name={conflictSettings.autoResolveMinor ? 'CheckCircle' : 'Circle'}
                    size={20}
                    color={
                      conflictSettings.autoResolveMinor
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'export' && (
          <div className="space-y-4">
            {isEditing ? (
              <>
                <Checkbox
                  label="Allow Member Export"
                  description="Members can export their own data"
                  checked={exportForm.allowMemberExport}
                  onChange={(e) =>
                    setExportForm({ ...exportForm, allowMemberExport: e.target.checked })
                  }
                />
                <Checkbox
                  label="Allow Contribution Export"
                  description="Enable contribution data export"
                  checked={exportForm.allowContributionExport}
                  onChange={(e) =>
                    setExportForm({ ...exportForm, allowContributionExport: e.target.checked })
                  }
                />
                <Checkbox
                  label="Allow Audit Log Export"
                  description="Enable audit log export for admins"
                  checked={exportForm.allowAuditLogExport}
                  onChange={(e) =>
                    setExportForm({ ...exportForm, allowAuditLogExport: e.target.checked })
                  }
                />
                <Select
                  label="Export Format"
                  description="Default format for data exports"
                  options={exportFormatOptions}
                  value={exportForm.exportFormat}
                  onChange={(value) =>
                    setExportForm({
                      ...exportForm,
                      exportFormat: value as ExportPermissions['exportFormat'],
                    })
                  }
                />
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">Member Export</span>
                  <Icon
                    name={exportPermissions.allowMemberExport ? 'CheckCircle' : 'Circle'}
                    size={20}
                    color={
                      exportPermissions.allowMemberExport
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">
                    Contribution Export
                  </span>
                  <Icon
                    name={exportPermissions.allowContributionExport ? 'CheckCircle' : 'Circle'}
                    size={20}
                    color={
                      exportPermissions.allowContributionExport
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">Audit Log Export</span>
                  <Icon
                    name={exportPermissions.allowAuditLogExport ? 'CheckCircle' : 'Circle'}
                    size={20}
                    color={
                      exportPermissions.allowAuditLogExport
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
                <div className="p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-muted-foreground">
                    Export Format
                  </span>
                  <p className="text-base text-foreground mt-1 uppercase">
                    {exportPermissions.exportFormat}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-4">
            {isEditing ? (
              <>
                <Checkbox
                  label="Contribution Reminders"
                  description="Send reminders for upcoming contributions"
                  checked={notificationForm.contributionReminders}
                  onChange={(e) =>
                    setNotificationForm({
                      ...notificationForm,
                      contributionReminders: e.target.checked,
                    })
                  }
                />
                <Checkbox
                  label="Recording Availability"
                  description="Notify when recording window opens"
                  checked={notificationForm.recordingAvailability}
                  onChange={(e) =>
                    setNotificationForm({
                      ...notificationForm,
                      recordingAvailability: e.target.checked,
                    })
                  }
                />
                <Checkbox
                  label="Approval Notifications"
                  description="Notify admins of pending approvals"
                  checked={notificationForm.approvalNotifications}
                  onChange={(e) =>
                    setNotificationForm({
                      ...notificationForm,
                      approvalNotifications: e.target.checked,
                    })
                  }
                />
                <Checkbox
                  label="Member Join Notifications"
                  description="Notify when new members join"
                  checked={notificationForm.memberJoinNotifications}
                  onChange={(e) =>
                    setNotificationForm({
                      ...notificationForm,
                      memberJoinNotifications: e.target.checked,
                    })
                  }
                />
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">
                    Contribution Reminders
                  </span>
                  <Icon
                    name={notificationSettings.contributionReminders ? 'CheckCircle' : 'Circle'}
                    size={20}
                    color={
                      notificationSettings.contributionReminders
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">
                    Recording Availability
                  </span>
                  <Icon
                    name={notificationSettings.recordingAvailability ? 'CheckCircle' : 'Circle'}
                    size={20}
                    color={
                      notificationSettings.recordingAvailability
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">
                    Approval Notifications
                  </span>
                  <Icon
                    name={notificationSettings.approvalNotifications ? 'CheckCircle' : 'Circle'}
                    size={20}
                    color={
                      notificationSettings.approvalNotifications
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
                  <span className="text-sm font-medium text-foreground">
                    Member Join Notifications
                  </span>
                  <Icon
                    name={
                      notificationSettings.memberJoinNotifications ? 'CheckCircle' : 'Circle'
                    }
                    size={20}
                    color={
                      notificationSettings.memberJoinNotifications
                        ? 'var(--color-success)'
                        : 'var(--color-muted-foreground)'
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {isEditing && (
          <div className="flex gap-3 pt-6 border-t border-border mt-6">
            <Button variant="default" onClick={handleSave}>
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedSettingsCard;